import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  /**
   * Уникальный идентификатор toast уведомления
   */
  id: string
  /**
   * Тип toast уведомления
   */
  type: ToastType
  /**
   * Текст сообщения
   */
  message: string
  /**
   * Длительность отображения в миллисекундах
   * @default 3000
   */
  duration?: number
  /**
   * Опциональное действие (кнопка) в toast уведомлении
   */
  action?: {
    /**
     * Текст кнопки действия
     */
    label: string
    /**
     * Обработчик клика на кнопку действия
     */
    onClick: () => void
  }
}

export interface ToastProps {
  toast: Toast
  onRemove: (id: string) => void
  /**
   * Callback для haptic feedback
   */
  onHapticFeedback?: (type: 'error' | 'success' | 'warning' | 'light') => void
}

/**
 * UI компонент: Toast уведомление
 * 
 * Компонент для отображения toast уведомлений.
 * 
 * @example
 * ```tsx
 * <ToastItem
 *   toast={toast}
 *   onRemove={handleRemove}
 * />
 * ```
 */
function ToastItem({ toast, onRemove, onHapticFeedback }: ToastProps) {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    // Haptic feedback при показе
    if (toast.type === 'error') {
      onHapticFeedback?.('error')
    } else if (toast.type === 'success') {
      onHapticFeedback?.('success')
    } else if (toast.type === 'warning') {
      onHapticFeedback?.('warning')
    }

    // Автоматическое скрытие
    const duration = toast.duration ?? 3000
    const timer = setTimeout(() => {
      setIsVisible(false)
      setTimeout(() => onRemove(toast.id), 200) // Задержка для анимации
    }, duration)

    return () => clearTimeout(timer)
  }, [toast, onRemove, onHapticFeedback])

  const icons = {
    success: <CheckCircle2 className="h-5 w-5" />,
    error: <AlertCircle className="h-5 w-5" />,
    info: <Info className="h-5 w-5" />,
    warning: <AlertTriangle className="h-5 w-5" />,
  }

  const styles = {
    success: 'bg-success text-white',
    error: 'bg-error text-white',
    info: 'bg-info text-white',
    warning: 'bg-warning text-white',
  }

  if (!isVisible) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg shadow-lg',
        'max-w-[90vw] w-full',
        styles[toast.type]
      )}
      role="alert"
      aria-live="polite"
    >
      <div className="flex-shrink-0 mt-0.5">
        {icons[toast.type]}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{toast.message}</p>
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              onHapticFeedback?.('light')
              toast.action?.onClick()
              setIsVisible(false)
              setTimeout(() => onRemove(toast.id), 200)
            }}
            className="mt-2 text-sm font-semibold underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          onHapticFeedback?.('light')
          setIsVisible(false)
          setTimeout(() => onRemove(toast.id), 200)
        }}
        className="flex-shrink-0 -mr-1 -mt-1 p-1 hover:bg-white/20 rounded transition-colors touch-target flex items-center justify-center"
        aria-label="Закрыть"
      >
        <X className="h-4 w-4" />
      </button>
    </motion.div>
  )
}

export interface ToastContainerProps {
  /**
   * Массив toast уведомлений для отображения
   */
  toasts: Toast[]
  /**
   * Обработчик удаления toast уведомления
   */
  onRemove: (id: string) => void
  /**
   * Максимальное количество одновременно отображаемых toast уведомлений
   * @default 3
   */
  maxToasts?: number
  /**
   * Callback для haptic feedback при взаимодействии с toast
   */
  onHapticFeedback?: (type: 'error' | 'success' | 'warning' | 'light') => void
}

/**
 * Контейнер для toast уведомлений
 * 
 * @example
 * ```tsx
 * <ToastContainer
 *   toasts={toasts}
 *   onRemove={handleRemove}
 *   maxToasts={3}
 * />
 * ```
 */
export function ToastContainer({
  toasts,
  onRemove,
  maxToasts = 3,
  onHapticFeedback,
}: ToastContainerProps) {
  const visibleToasts = toasts.slice(0, maxToasts)

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 pointer-events-none"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <AnimatePresence mode="popLayout">
        {visibleToasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem toast={toast} onRemove={onRemove} onHapticFeedback={onHapticFeedback} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  )
}

// Toast Manager Hook
export interface UseToastReturn {
  toasts: Toast[]
  show: (toast: Omit<Toast, 'id'>) => void
  remove: (id: string) => void
  success: (message: string, options?: Partial<Toast>) => void
  error: (message: string, options?: Partial<Toast>) => void
  info: (message: string, options?: Partial<Toast>) => void
  warning: (message: string, options?: Partial<Toast>) => void
}

/**
 * Хук для управления toast уведомлениями
 * 
 * Предоставляет методы для создания и управления toast уведомлениями.
 * Каждый вызов метода создает новое уведомление с уникальным ID.
 * 
 * @returns Объект с методами управления toast уведомлениями
 * - `toasts`: массив текущих toast уведомлений
 * - `show`: универсальный метод для создания toast
 * - `remove`: метод для удаления toast по ID
 * - `success`: метод для создания success toast
 * - `error`: метод для создания error toast
 * - `info`: метод для создания info toast
 * - `warning`: метод для создания warning toast
 * 
 * @example
 * ```tsx
 * const { toasts, success, error, remove } = useToast()
 * 
 * const handleSave = async () => {
 *   try {
 *     await saveData()
 *     success('Успешно сохранено!')
 *   } catch (err) {
 *     error('Ошибка при сохранении')
 *   }
 * }
 * 
 * return (
 *   <>
 *     <button onClick={handleSave}>Сохранить</button>
 *     <ToastContainer toasts={toasts} onRemove={remove} />
 *   </>
 * )
 * ```
 */
export function useToast(): UseToastReturn {
  const [toasts, setToasts] = useState<Toast[]>([])

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }

  const addToast = (toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random()}`
    setToasts((prev) => [...prev, { ...toast, id }])
  }

  return {
    toasts,
    show: addToast,
    remove: removeToast,
    success: (message: string, options?: Partial<Toast>) => {
      addToast({ type: 'success', message, ...options })
    },
    error: (message: string, options?: Partial<Toast>) => {
      addToast({ type: 'error', message, ...options })
    },
    info: (message: string, options?: Partial<Toast>) => {
      addToast({ type: 'info', message, ...options })
    },
    warning: (message: string, options?: Partial<Toast>) => {
      addToast({ type: 'warning', message, ...options })
    },
  }
}

